import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { CandidateStatus } from '@cvx/features/candidates/candidates_db'
import { Button } from '@rekrytering/ui/components/button'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Star } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as InterviewReportRoute } from '@/routes/_app/_auth/dashboard/_layout.interviews.$interviewId'

const CANDIDATE_STATUS_STYLES: Record<CandidateStatus, string> = {
  invited:
    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  started: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-900/30 dark:text-gray-400',
  disqualified: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  advanced:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
}

type SortKey = 'name' | 'status' | 'score'
type SortDir = 'asc' | 'desc'

type Props = {
  readonly jobId: Id<'jobs'>
}

export const OverviewTab = ({ jobId }: Props) => {
  const { t } = useTranslation()
  const [sortKey, setSortKey] = useState<SortKey>('score')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [shortlistedOnly, setShortlistedOnly] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)

  const { data: candidates = [] } = useQuery(
    convexQuery(api.features.candidates.candidates.listMyCandidatesWithScores, {
      jobId,
    }),
  )

  const { mutateAsync: toggleShortlist } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.toggleMyCandidateShortlist,
    ),
  })

  const { mutateAsync: generateShareToken } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidates.generateMyShortlistShareToken,
    ),
  })

  const handleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'score' ? 'desc' : 'asc')
    }
  }

  const handleShareShortlist = async (): Promise<void> => {
    const token = await generateShareToken({ jobId })
    const url = `${window.location.origin}/sl/${token}`
    await navigator.clipboard.writeText(url)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const visible = shortlistedOnly
    ? candidates.filter((c) => c.shortlisted)
    : candidates

  const sorted = [...visible].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    if (sortKey === 'name') return a.name.localeCompare(b.name) * dir
    if (sortKey === 'status') return a.status.localeCompare(b.status) * dir
    const aScore = a.averageScore ?? -1
    const bScore = b.averageScore ?? -1
    return (aScore - bScore) * dir
  })

  if (candidates.length === 0) {
    return (
      <div className="flex items-center justify-center py-24 text-primary/40">
        <p className="text-sm">{t('interviews.candidates.noInterviews')}</p>
      </div>
    )
  }

  const SortIndicator = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <span className="ml-1 text-primary/20">↕</span>
    return (
      <span className="ml-1 text-primary/60">
        {sortDir === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setShortlistedOnly((prev) => !prev)}
          className={cn(
            'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
            shortlistedOnly
              ? 'border-yellow-400 bg-yellow-50 text-yellow-700 dark:border-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'
              : 'border-border bg-background text-primary/60 hover:text-primary',
          )}
        >
          <Star
            className={cn(
              'h-3.5 w-3.5',
              shortlistedOnly ? 'fill-yellow-400 text-yellow-400' : '',
            )}
          />
          {t('jobs.overview.shortlistedOnly')}
        </button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleShareShortlist}
        >
          {linkCopied
            ? t('jobs.overview.linkCopied')
            : t('jobs.overview.shareShortlist')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-primary/50">
              <th className="w-8 py-2 pr-2 font-medium" />
              <th
                className="cursor-pointer py-2 pr-4 font-medium hover:text-primary"
                onClick={() => handleSort('name')}
              >
                {t('jobs.overview.name')}
                <SortIndicator col="name" />
              </th>
              <th className="py-2 pr-4 font-medium">
                {t('jobs.overview.email')}
              </th>
              <th
                className="cursor-pointer py-2 pr-4 font-medium hover:text-primary"
                onClick={() => handleSort('status')}
              >
                {t('jobs.overview.status')}
                <SortIndicator col="status" />
              </th>
              <th
                className="cursor-pointer py-2 pr-4 font-medium hover:text-primary"
                onClick={() => handleSort('score')}
              >
                {t('jobs.overview.score')}
                <SortIndicator col="score" />
              </th>
              <th className="py-2 font-medium">{t('jobs.overview.report')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((candidate) => (
              <tr
                key={candidate._id}
                className="border-b border-border/50 last:border-0"
              >
                <td className="py-3 pr-2">
                  <button
                    type="button"
                    onClick={() =>
                      toggleShortlist({ candidateId: candidate._id })
                    }
                    aria-label={t('jobs.overview.shortlist')}
                    className="text-primary/30 transition hover:text-yellow-400"
                  >
                    <Star
                      className={cn(
                        'h-4 w-4',
                        candidate.shortlisted
                          ? 'fill-yellow-400 text-yellow-400'
                          : '',
                      )}
                    />
                  </button>
                </td>
                <td className="py-3 pr-4 font-medium text-primary">
                  {candidate.name}
                </td>
                <td className="py-3 pr-4 text-primary/60">{candidate.email}</td>
                <td className="py-3 pr-4">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      CANDIDATE_STATUS_STYLES[candidate.status],
                    )}
                  >
                    {candidate.status}
                  </span>
                </td>
                <td className="py-3 pr-4 tabular-nums text-primary">
                  {candidate.averageScore !== null
                    ? Math.round(candidate.averageScore)
                    : t('jobs.overview.noScore')}
                </td>
                <td className="py-3">
                  {candidate.interviewId !== null ? (
                    <Link
                      to={InterviewReportRoute.fullPath}
                      params={{ interviewId: candidate.interviewId }}
                      className="text-xs text-primary/60 hover:text-primary hover:underline"
                    >
                      {t('jobs.overview.viewReport')}
                    </Link>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
